/* eslint-disable no-restricted-globals */
/* eslint-disable no-unused-expressions */
import React from 'react';

import 'chartjs-plugin-zoom';
import Chart from 'chart.js';
import PropTypes from 'prop-types';

import Tooltip from '@material-ui/core/Tooltip';
import { connect, useSelector } from 'react-redux';
import { Typography } from '@material-ui/core';

import Icons from 'consts/icons';
import UserSessionDataHandler from 'auth/UserSessionsDataHandler';
import { approvedAreaChartStyle } from 'consts/colors';
import {
  getApprovedAreaPartsLength,
  removeLineStringFromApprovedArea,
  returnLineLevelFromApprovedArea,
  serializeData,
} from 'components/approvedAreas/utils/main';

import { useStyles } from './ApprovedAreasChartUseStyles';

const CorridorChart = props => {
  const { language, foundArea, chartCases, heightUnit } = props;
  const classes = useStyles(props);
  const [cleanedApprovedArea, setCleanedApprovedArea] = React.useState(null);
  const [corridorBottomLine, setCorridorBottomLine] = React.useState([]);
  const [corridorTopLine, setCorridorTopLine] = React.useState([]);
  const [groundHeightLine, setGroundHeight] = React.useState([]);
  const [approvedAreaPartLength, setApprovedAreaPartLength] = React.useState(
    []
  );
  const [chartCanvas, setChartCanvas] = React.useState(null);
  const [chartZoomLimit] = React.useState(40);

  const useString = () => {
    // eslint-disable-next-line no-shadow
    const language = useSelector(state => state.language);

    return language;
  };
  const subtitles = useString().areas.list.notPolygon;

  React.useEffect(() => {
    setCleanedApprovedArea(
      removeLineStringFromApprovedArea(JSON.parse(JSON.stringify(foundArea)))
    );
  }, [foundArea]);

  React.useEffect(() => {
    if (!cleanedApprovedArea) return;
    setApprovedAreaPartLength(getApprovedAreaPartsLength(cleanedApprovedArea));
    setCorridorBottomLine(
      returnLineLevelFromApprovedArea(
        cleanedApprovedArea,
        foundArea.agl_max,
        heightUnit === 'm'
      )
    );
    setCorridorTopLine(
      returnLineLevelFromApprovedArea(
        cleanedApprovedArea,
        foundArea.agl_min,
        heightUnit === 'm'
      )
    );
    setGroundHeight(
      returnLineLevelFromApprovedArea(
        cleanedApprovedArea,
        0,
        heightUnit === 'm'
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanedApprovedArea]);

  React.useEffect(() => {
    if (chartCanvas) {
      resetZoom();
      chartCanvas.destroy();
    }
    approvedAreaPartLength[1] !== undefined &&
      setChartCanvas(
        new Chart(
          document
            .getElementById(`areaChart+${foundArea.uid.slice(3, 8)}`)
            .getContext('2d'),
          {
            type: 'scatter',
            data: {
              datasets: [
                {
                  fill: true,
                  backgroundColor:
                    approvedAreaChartStyle.colors.groundEdgeColor,
                  lineTension: 0,
                  label: language.approvedAreasDeatils.approvedAreaGroundLevel_,
                  data: serializeData(approvedAreaPartLength, groundHeightLine),
                  borderColor: approvedAreaChartStyle.colors.groundColor,
                  showLine: true,
                },
                {
                  backgroundColor: approvedAreaChartStyle.colors.corridorColor,
                  fill: false,
                  color: approvedAreaChartStyle.colors.corridorColor,
                  lineTension: 0,
                  label: language.approvedAreasDeatils.approvedAreaBottomLevel_,
                  data: serializeData(
                    approvedAreaPartLength,
                    corridorBottomLine,
                    chartCases
                  ),
                  showLine: true,
                },
                {
                  color: approvedAreaChartStyle.colors.corridorColor,
                  backgroundColor: approvedAreaChartStyle.colors.corridorColor,
                  lineTension: 0,
                  label: language.approvedAreasDeatils.approvedAreaTopLevel_,
                  data: serializeData(
                    approvedAreaPartLength,
                    corridorTopLine,
                    chartCases
                  ),
                  showLine: true,
                  fill: '-1',
                },
              ],
            },

            options: {
              title: {
                display: true,
                text: `${language.approvedAreasDeatils.chartTitle_} - ${foundArea.othername}`,
              },
              elements: {
                point: {
                  radius: 1,
                },
              },
              legend: {
                display: false,
                align: 'start',
                position: 'bottom',
              },
              scales: {
                xAxes: [
                  {
                    ticks: {
                      min: 0,
                      max: approvedAreaPartLength.slice(-1).pop(),
                    },
                    scaleLabel: {
                      display: true,
                      labelString:
                        language.approvedAreasDeatils.approvedAreaLength_,
                    },
                  },
                ],
                yAxes: [
                  {
                    scaleLabel: {
                      display: true,
                      labelString:
                        language.approvedAreasDeatils.overSeaHeightLevel_,
                    },
                    ticks: {
                      min: Math.min(...groundHeightLine) - chartZoomLimit,
                      callback(value, index) {
                        if (index % 3 === 0) {
                          return value.toFixed(2);
                        }
                        return ' ';
                      },
                    },
                  },
                ],
              },

              pan: {
                enabled: true,
                mode: 'xy',
                rangeMin: {
                  x: 0,
                  y: Math.min(...groundHeightLine) - chartZoomLimit,
                },
                rangeMax: {
                  x: approvedAreaPartLength.slice(-1).pop(),
                  y: Math.max(...corridorTopLine) + chartZoomLimit,
                },
              },
              zoom: {
                enabled: 'true',
                mode: 'xy',
                rangeMin: {
                  x: 0,
                  y: Math.min(...groundHeightLine) - chartZoomLimit,
                },
                rangeMax: {
                  x: approvedAreaPartLength.slice(-1).pop(),
                  y: Math.max(...corridorTopLine) + chartZoomLimit,
                },
                onZoomComplete() {
                  document.getElementById(
                    `resetZoomButton+${foundArea.uid.slice(3, 8)}`
                  ).style.display = 'flex';
                },
              },
              maintainAspectRatio: false,
              tooltips: {
                callbacks: {
                  label(tooltipItem, data) {
                    const { label } = data.datasets[tooltipItem.datasetIndex];
                    return `${label}: (${tooltipItem.xLabel}${
                      language.approvedAreasDeatils.kilometerUnit_
                    }, ${tooltipItem.yLabel}${
                      heightUnit === 'm'
                        ? language.approvedAreasDeatils.meterUnit_
                        : language.approvedAreasDeatils.feetUnit_
                    })`;
                  },
                },
              },
            },
          }
        )
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groundHeightLine, chartCases]);

  React.useEffect(() => {
    if (UserSessionDataHandler.getTheme() === 'light') {
      Chart.defaults.global.defaultFontColor = 'rgb(37, 36, 34)';
    }
    if (UserSessionDataHandler.getTheme() === 'dark') {
      Chart.defaults.global.defaultFontColor = 'rgba(255, 255, 255, 0.7)';
    }
    if (chartCanvas) chartCanvas.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UserSessionDataHandler.getTheme()]);

  const resetZoom = () => {
    chartCanvas.resetZoom();
    document.getElementById(
      `resetZoomButton+${foundArea.uid.slice(3, 8)}`
    ).style.display = 'none';
  };

  return (
    <div
      className={
        approvedAreaPartLength[1] === undefined ? '' : classes.ChartCalc
      }
    >
      <Tooltip title={`${language.approvedAreasDeatils.resetZoomMessage}`}>
        <div
          role="button"
          id={`resetZoomButton+${foundArea.uid.slice(3, 8)}`}
          className={`${classes.Chart}__reset-zoom-container`}
          onClick={() => resetZoom()}
          aria-hidden="true"
        >
          <Icons.Actions.Reset
            className={`${classes.Chart}__reset-zoom-container__icon-reset-zoom`}
          />
        </div>
      </Tooltip>
      {approvedAreaPartLength[1] === undefined ? (
        <Typography
          variant="h5"
          align="center"
          className={`${classes.Chart}--warning`}
        >
          {subtitles}
        </Typography>
      ) : (
        <canvas
          className={`${classes.Chart}__chart`}
          id={`areaChart+${foundArea.uid.slice(3, 8)}`}
        />
      )}
    </div>
  );
};

CorridorChart.propTypes = {
  foundArea: PropTypes.object.isRequired,
  language: PropTypes.object.isRequired,
  chartCases: PropTypes.bool,
  heightUnit: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({
  language: state.language,
  heightUnit: state.heightUnit,
});
export default connect(mapStateToProps)(CorridorChart);
